// Insights et leurs PREUVES.
//
// C'est ici que se joue la promesse « Atomic Research » : un insight cite les
// faits qui l'établissent, et chaque fait dit qui l'a prononcé, dans quel
// document, à quelle seconde. Un insight sans preuve n'est pas une conclusion,
// c'est une opinion — et l'interface doit le montrer, pas le masquer.
import { useState } from 'react';
import { useEvidence, useInsights, useObjectives } from '../api/queries';
import type { Evidence, HypothesisStatus, Insight, Sentiment } from '../api/types';
import { HYPOTHESIS_STATUS_LABEL } from '../insights/labels';
import { Button, buttonClass, Chip, EmptyState, ErrorBanner, Eyebrow, Note, Select, Spinner } from '../components/ui';
import { useCanWrite } from '../write/useCanWrite';
import { FilterNote, LifecycleChip, useProjectFilter } from '../lifecycle/ProjectFilter';
import { EditAction, NewInsightForm, WriteAction } from '../write/Forms';
import { Link } from '../router';
import { PriorityChip, ProblemTypeChip, RecoKindChip, SeverityChip, StatusChip } from '../taxonomy/Chips';
import { DigInsight } from '../insights/DigInsight';
import { ProblemPanel } from '../insights/ProblemPanel';
import { OpportunityPanel } from '../insights/OpportunityPanel';
import '../insights/insights.css';

const SENTIMENT_LABEL: Record<Sentiment, string> = {
  pos: 'positif',
  neu: 'neutre',
  neg: 'négatif',
};

// Un insight étayé (≥ 1 preuve) n'est pas encore opposable pour autant : une
// preuve isolée peut être un cas particulier. Trois preuves indépendantes,
// c'est ce qui distingue une observation d'une triangulation — le seuil que
// la recherche qualitative retient pour parler de signal établi plutôt que
// d'anecdote.
const VALIDATED_EVIDENCE_MIN = 3;

function formatTimecode(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

function EvidenceList({ insightId }: { insightId: string }) {
  const evidence = useEvidence(insightId);

  if (evidence.isPending) {
    return (
      <div className="evidence-loading">
        <Spinner label="Chargement des preuves…" />
      </div>
    );
  }
  if (evidence.isError) {
    return <ErrorBanner>Impossible de charger les preuves.</ErrorBanner>;
  }
  if (evidence.data.length === 0) {
    return (
      <p className="evidence-empty">
        Aucun fait rattaché. Cet insight n’est pas opposable en l’état.
      </p>
    );
  }

  return (
    <ul className="evidence-list">
      {evidence.data.map((e: Evidence) => {
        // « Qui l'a dit, dans quel document, à quelle seconde. » On ne compose
        // la ligne qu'avec ce qui existe réellement — pas de séparateurs
        // orphelins autour d'un champ vide.
        const meta = [
          e.persona_name ?? e.speaker,
          e.source_ref,
          formatTimecode(e.ts_ms),
        ].filter(Boolean);
        // Une mesure n'est pas une citation : on ne la met pas entre guillemets
        // et on la signale comme telle. Les deux sont des preuves, elles ne se
        // lisent simplement pas de la même façon.
        const isMeasure = e.kind === 'test_feature';
        return (
          <li key={e.id} className="evidence">
            <span className={`dot dot-${e.sentiment ?? 'neu'}`} aria-hidden="true" />
            <span className="evidence-body">
              <span className="evidence-text">
                {isMeasure ? <Chip className="evidence-kind">mesure</Chip> : null}
                {isMeasure ? e.text : `« ${e.text} »`}
              </span>
              {meta.length > 0 ? <span className="evidence-meta">{meta.join(' · ')}</span> : null}
            </span>
            {e.sentiment ? (
              <span className="sr-only">Sentiment {SENTIMENT_LABEL[e.sentiment]}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function InsightCard({
  insight,
  projectId,
  canWrite,
  parentTitle,
}: {
  insight: Insight;
  projectId: string;
  canWrite: boolean;
  /** Titre de l'insight dont celui-ci a été creusé, s'il est encore là. */
  parentTitle: string | null;
}) {
  // La carte s'ouvre en APERÇU : numéro, titre, pastilles, résumé. Tout le
  // reste (interprétation, preuves, « Creuser ») se déroule au clic.
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const supported = insight.evidence_count > 0;
  const validated = insight.evidence_count >= VALIDATED_EVIDENCE_MIN;
  const gaps = insight.quality.flags.filter((f) => f.code !== 'no_evidence');

  return (
    <li className={`card insight${supported ? '' : ' insight-unsupported'}${expanded ? ' insight-open' : ''}`}>
      <div className="card-head">
        {/* Toute la zone de gauche déroule la carte ; le titre est un vrai
            bouton, pour que cela marche aussi au clavier. */}
        <div className="insight-main" onClick={() => setExpanded((v) => !v)}>
          <Eyebrow>{insight.id}</Eyebrow>
          <h2 className="t-h3">
            <button
              type="button"
              className="insight-toggle"
              aria-expanded={expanded}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              {insight.title}
              <span className="insight-chevron" aria-hidden="true">{expanded ? '▴' : '▾'}</span>
            </button>
          </h2>
          {insight.summary ? (
            <p className={`insight-preview${expanded ? ' insight-preview-full' : ''}`}>{insight.summary}</p>
          ) : null}
          {insight.depth > 0 ? (
            <p className="insight-origin">
              <Chip hue="violet">Creusé · niveau {insight.depth}</Chip>
              {parentTitle ? <span>depuis « {parentTitle} »</span> : null}
            </p>
          ) : null}
        </div>
        <div className="card-head-right">
          {validated ? (
            <Chip state="succes" title={`${insight.evidence_count} preuves rattachées`}>
              Validé
            </Chip>
          ) : null}
          {insight.origin === 'ia' ? (
            <Chip ia title="Proposé par la dérivation depuis une étude, puis accepté">
              IA
            </Chip>
          ) : null}
          <PriorityChip priority={insight.problem.priority} />
          <ProblemTypeChip type={insight.problem_type} />
          <SeverityChip value={insight.severity} />
          <StatusChip value={insight.treatment} />
          <LifecycleChip projectId={projectId} kind="insight" id={insight.id} phase={insight.phase} version={insight.version} canWrite={canWrite} />
          {canWrite && expanded ? (
            <EditAction
              title="Modifier l’insight"
              description="Le constat, le pourquoi, l’opportunité et la recommandation. Les faits cités ne sont pas touchés ici."
            >
              {(close) => <NewInsightForm projectId={projectId} existing={insight} onDone={close} />}
            </EditAction>
          ) : null}
        </div>
      </div>

      {expanded ? (
      <div className="insight-detail">
      {/* L'interprétation : pourquoi, ce que cela ouvre, et ce qu'on recommande. */}
      {insight.why || insight.opportunity || insight.recommendation ? (
        <dl className="insight-parts">
          {insight.why ? (
            <div>
              <dt>Pourquoi</dt>
              <dd>{insight.why}</dd>
            </div>
          ) : null}
          {insight.opportunity ? (
            <div>
              <dt>Opportunité</dt>
              <dd>{insight.opportunity}</dd>
            </div>
          ) : null}
          {insight.recommendation ? (
            <div className="insight-part-reco">
              <dt>
                Recommandation
                <RecoKindChip kind={insight.recommendation_kind} />
              </dt>
              <dd>{insight.recommendation}</dd>
            </div>
          ) : null}
        </dl>
      ) : null}

      {/* Le problème qualifié : qui, combien, avec quelle confiance — et sa priorité. */}
      <ProblemPanel insight={insight} projectId={projectId} canWrite={canWrite} />

      {/* L'opportunité : qui on vise, ce qu'on parie, ce qui tranchera. */}
      <OpportunityPanel insight={insight} projectId={projectId} canWrite={canWrite} />

      {/* À quel objectif de recherche il répond : lu par ses preuves. */}
      {insight.studies.length > 0 ? (
        <p className="insight-objective">
          Répond à l’objectif de{' '}
          {insight.studies.map((s, idx) => (
            <span key={s.id}>
              {idx > 0 ? ', ' : ''}
              <Link to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(s.id)}`}>
                {s.code ?? s.id}
              </Link>
              {s.objective ? ` — ${s.objective}` : ''}
            </span>
          ))}
        </p>
      ) : null}

      <div className="insight-foot">
        {supported ? (
          <Button variant="ghost" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? 'Masquer' : 'Voir'} les {insight.evidence_count} preuve
            {insight.evidence_count > 1 ? 's' : ''}
            {!validated ? ` (${VALIDATED_EVIDENCE_MIN - insight.evidence_count} avant validation)` : ''}
          </Button>
        ) : (
          <p className="unsupported-note">
            Aucun fait cité — cet insight n’est qu’une opinion tant qu’il n’est pas étayé.
          </p>
        )}
        {insight.jira_ref ? <Chip>{insight.jira_ref}</Chip> : null}
      </div>

      {open ? <EvidenceList insightId={insight.id} /> : null}

      {/* Aller plus loin : l'IA propose un insight plus profond, à ajouter au projet. */}
      {canWrite && supported ? <DigInsight insight={insight} projectId={projectId} /> : null}

      {/* Ce qui manque pour qu'il soit exploitable. L'absence de preuve est
          déjà dite juste au-dessus : on ne la répète pas. */}
      {gaps.length > 0 ? (
        <ul className="insight-flags" aria-label="À compléter">
          {gaps.map((f) => (
            <li key={f.code}>{f.text}</li>
          ))}
        </ul>
      ) : null}
      </div>
      ) : null}
    </li>
  );
}

export function InsightsScreen({ projectId }: { projectId: string }) {
  const query = useInsights(projectId);
  const canWrite = useCanWrite(projectId);
  const filter = useProjectFilter();
  // « À compléter » : ne garder que les insights auxquels il manque quelque chose.
  const [onlyGaps, setOnlyGaps] = useState(false);
  // Par défaut, ce qu'il faut traiter d'abord ; les plus récents sur demande.
  const [byDate, setByDate] = useState(false);
  // Par objectif du projet (migration 049) : '' = tous, 'none' = sans objectif.
  const objectives = useObjectives(projectId).data?.objectives ?? [];
  const [objectiveFilter, setObjectiveFilter] = useState('');
  // Par statut d'hypothèse (migration 051) : '' = tous.
  const [statusFilter, setStatusFilter] = useState<HypothesisStatus | ''>('');
  const inFilter = (query.data ?? []).filter(filter.matches);
  const incomplete = inFilter.filter((i) => i.quality.level !== 'ok').length;
  const gapsKept = onlyGaps && incomplete > 0 ? inFilter.filter((i) => i.quality.level !== 'ok') : inFilter;
  const byObjective = objectiveFilter === '' ? gapsKept
    : gapsKept.filter((i) => (objectiveFilter === 'none' ? !i.objective_id : i.objective_id === objectiveFilter));
  // Une hypothèse n'a de statut que si elle est formulée.
  const kept = statusFilter === '' ? byObjective
    : byObjective.filter((i) => i.hypothesis && i.hypothesis_status === statusFilter);
  // Le serveur renvoie les plus récents d'abord : un tri stable les garde dans
  // cet ordre à priorité égale. Sans preuve, pas de priorité : en dernier.
  const shown = byDate ? kept : [...kept].sort((a, b) => (b.problem.priority?.score ?? -1) - (a.problem.priority?.score ?? -1));

  const unsupported = shown.filter((i) => i.evidence_count === 0).length;
  const titles = new Map((query.data ?? []).map((i) => [i.id, i.title]));

  return (
    <main className="screen">
      <div className="zy-page-head__texte">
        <Eyebrow>Insights</Eyebrow>
        <h1 className="t-h1">Insights</h1>
        <p className="zy-intro">
          Les conclusions tirées du terrain, et les faits qui les établissent.
        </p>
        {canWrite ? (
          <div className="screen-actions">
            {/* Le mode MANUEL : ranger soi-même les verbatims en post-it. */}
            <Link to={`/projet/${encodeURIComponent(projectId)}/atelier`} className={buttonClass('ghost')}>
              Gérer à la main dans l’atelier
            </Link>
            <WriteAction
              label="Nouvel insight"
              accent
              title="Nouvel insight"
              description="Un insight cite les faits qui l’établissent. Sans preuve, le serveur le refuse — c’est ce qui rend vos conclusions opposables."
            >
              {(close) => <NewInsightForm projectId={projectId} onDone={close} />}
            </WriteAction>
          </div>
        ) : null}
      </div>

      {query.isPending ? (
        <div className="screen-loading">
          <Spinner label="Chargement des insights…" />
        </div>
      ) : query.isError ? (
        <>
          <ErrorBanner>
            {query.error instanceof Error ? query.error.message : 'Chargement impossible.'}
          </ErrorBanner>
          <Button variant="ghost" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        </>
      ) : query.data.length === 0 ? (
        <EmptyState
          title="Aucun insight pour l’instant"
          description="Un insight se déduit des faits collectés. Ouvrez une étude et lancez « Dériver des insights » pour en obtenir à partir de ses verbatims, ou créez-en un à la main — il exige au moins une preuve."
        />
      ) : (
        <>
          {unsupported > 0 ? (
            <Note tone="avertissement">
              <strong>{unsupported}</strong> insight{unsupported > 1 ? 's' : ''} ne cite aucun
              fait. Ils proviennent d’un import externe, dont la provenance est ailleurs — mais on
              ne peut pas s’appuyer dessus comme sur les autres.
            </Note>
          ) : null}
          {incomplete > 0 ? (
            <div className="quality-bar">
              <Note tone="avertissement">
                <strong>{incomplete}</strong> insight{incomplete > 1 ? 's' : ''} à compléter : un insight
                exploitable cite ses preuves, dit <em>pourquoi</em>, et se termine par une recommandation
                précise.
              </Note>
              <Button variant="ghost" onClick={() => setOnlyGaps((v) => !v)} aria-pressed={onlyGaps}>
                {onlyGaps ? 'Tout afficher' : 'Voir ceux à compléter'}
              </Button>
            </div>
          ) : null}
          <FilterNote shown={inFilter.length} total={query.data.length} />
          <div className="insight-sort">
            <Button variant="ghost" onClick={() => setByDate((v) => !v)} aria-pressed={!byDate}>
              {byDate ? 'Trier par priorité' : 'Trier par date'}
            </Button>
            <span className="insight-sort-note">
              {byDate ? 'Les plus récents d’abord.' : 'Les plus prioritaires d’abord : gravité × fréquence × impact ÷ coût, selon la confiance et l’objectif servi.'}
            </span>
            {objectives.length > 0 ? (
              <Select
                className="insight-objective-filter"
                aria-label="Filtrer par objectif"
                value={objectiveFilter}
                onChange={(e) => setObjectiveFilter(e.target.value)}
              >
                <option value="">Tous les objectifs</option>
                {objectives.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                <option value="none">Sans objectif</option>
              </Select>
            ) : null}
            {(query.data ?? []).some((i) => i.hypothesis) ? (
              <Select
                className="insight-objective-filter"
                aria-label="Filtrer par statut d’hypothèse"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as HypothesisStatus | '')}
              >
                <option value="">Toutes les hypothèses</option>
                {(Object.keys(HYPOTHESIS_STATUS_LABEL) as HypothesisStatus[]).map((s) => (
                  <option key={s} value={s}>{HYPOTHESIS_STATUS_LABEL[s]}</option>
                ))}
              </Select>
            ) : null}
          </div>
          {shown.length === 0 ? (
            <p className="zy-texte-muet">Rien ne correspond au filtre du projet.</p>
          ) : (
            <ul className="card-list">
              {shown.map((i) => (
                <InsightCard key={i.id} insight={i} projectId={projectId} canWrite={canWrite} parentTitle={i.parent_id ? (titles.get(i.parent_id) ?? null) : null} />
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
